import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Search, ArrowLeft, Megaphone, Calendar, Users, TrendingUp,
  Thermometer, ShoppingBasket, Armchair, Loader2, ChevronRight,
  X, SlidersHorizontal, ArrowRight, Target, ZoomIn,
} from "lucide-react";
import axios from "axios";
import { PUBLIC_API } from "../lib/api";
import { chiffresCarte, lireEtat } from "../lib/etatCampagne";
import TeroumaLogo from "../components/ui/TeroumaLogo";
import CampaignImageLightbox from "../components/shared/CampaignImageLightbox";

const fmtEUR = (v) =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(Number(v || 0));

// Backend image_url is relative (/api/...). We prefix the backend origin so
// the <img> resolves even when frontend and backend live on different domains.
const BACKEND_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

const TYPE_META = {
  thermometer:    { label: "Don libre",      Icon: Thermometer,    color: "#0D1B33" },
  unit_qty:       { label: "Unité × Qté",    Icon: ShoppingBasket, color: "#B8962E" },
  seats_request:  { label: "Réservation",    Icon: Armchair,       color: "#1D4ED8" },
};

const SORTS = [
  { value: "recent",    label: "Plus récentes" },
  { value: "popular",   label: "Plus populaires" },
  { value: "ending",    label: "Bientôt terminées" },
  { value: "progress",  label: "Plus avancées" },
];

export default function AllCampaignsPage() {
  const [campaigns, setCampaigns] = useState(null);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [sortBy, setSortBy] = useState("recent");
  const [error, setError] = useState(false);

  useEffect(() => {
    axios
      .get(`${PUBLIC_API}/campaigns/public`, { params: { limit: 100 } })
      .then((r) => setCampaigns(r.data?.campaigns || []))
      .catch(() => setError(true))
      .finally(() => {
        if (campaigns === null) setCampaigns((prev) => prev ?? []);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Global stats (top hero)
  const globalStats = useMemo(() => {
    if (!campaigns || campaigns.length === 0) {
      return { count: 0, collected: 0, donors: 0 };
    }
    let collected = 0;
    let donors = 0;
    for (const c of campaigns) {
      collected += c.stats?.collected || 0;
      donors += c.stats?.donors_count || 0;
    }
    return { count: campaigns.length, collected, donors };
  }, [campaigns]);

  // Derived filtered + sorted list
  const filtered = useMemo(() => {
    if (!campaigns) return [];
    let list = [...campaigns];
    if (typeFilter !== "all") {
      list = list.filter((c) => c.type === typeFilter);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((c) =>
        (c.title || "").toLowerCase().includes(q) ||
        (c.organization_name || "").toLowerCase().includes(q) ||
        (c.description || "").toLowerCase().includes(q)
      );
    }
    const safeDate = (s) => (s ? new Date(s).getTime() : 0);
    if (sortBy === "ending") {
      list.sort((a, b) => {
        const da = a.end_date ? safeDate(a.end_date) : Number.MAX_SAFE_INTEGER;
        const db = b.end_date ? safeDate(b.end_date) : Number.MAX_SAFE_INTEGER;
        return da - db;
      });
    } else if (sortBy === "popular") {
      list.sort((a, b) => (b.stats?.donors_count || 0) - (a.stats?.donors_count || 0));
    } else if (sortBy === "progress") {
      // Même pourcentage que la carte et la page campagne (le promis).
      const avancement = (c) => lireEtat(c).pourcentage ?? c.stats?.progress_pct ?? 0;
      list.sort((a, b) => avancement(b) - avancement(a));
    }
    return list;
  }, [campaigns, search, typeFilter, sortBy]);

  // Count badges per type
  const typeCounts = useMemo(() => {
    const counts = { all: 0, thermometer: 0, unit_qty: 0, seats_request: 0 };
    (campaigns || []).forEach((c) => {
      counts.all += 1;
      if (counts[c.type] !== undefined) counts[c.type] += 1;
    });
    return counts;
  }, [campaigns]);

  const activeFilters = (typeFilter !== "all" ? 1 : 0) + (search.trim() ? 1 : 0);

  return (
    <div className="min-h-screen" style={{ background: "#F4F3EF", fontFamily: "'Outfit', sans-serif" }}>
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-stone-200/60">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <TeroumaLogo textColor="#0D1B33" />
          </Link>
          <div className="flex items-center gap-5">
            <Link to="/" className="hidden sm:inline-flex items-center gap-1.5 text-sm text-stone-600 hover:text-stone-900 transition-colors">
              <ArrowLeft className="h-4 w-4" /> Accueil
            </Link>
            <Link to="/contact" className="text-sm text-stone-600 hover:text-stone-900 transition-colors hidden sm:inline">
              Contact
            </Link>
          </div>
        </div>
      </nav>

      {/* ─── HERO PREMIUM ─────────────────────────────────────────────── */}
      <section className="relative overflow-hidden" style={{ background: "#0D1B33" }}>
        {/* Decorative gradient orbs */}
        <div
          className="absolute -top-32 -left-20 h-96 w-96 rounded-full opacity-30 blur-3xl"
          style={{ background: "radial-gradient(circle, #B8962E 0%, transparent 70%)" }}
        />
        <div
          className="absolute -bottom-24 -right-32 h-[28rem] w-[28rem] rounded-full opacity-20 blur-3xl"
          style={{ background: "radial-gradient(circle, #B8962E 0%, transparent 70%)" }}
        />

        <div className="relative max-w-6xl mx-auto px-6 py-16 sm:py-24 text-center">
          {/* Eyebrow — typographic only */}
          <p className="text-[11px] uppercase mb-6"
             style={{ color: "#D4B45C", letterSpacing: "0.28em", fontWeight: 500 }}>
            Toutes les collectes actives
          </p>

          {/* Title */}
          <h1
            className="text-white text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight mb-5"
            style={{ fontFamily: "'Space Grotesk', sans-serif", lineHeight: 1.05 }}
          >
            Soutenir <em className="not-italic" style={{ color: "#D4B45C" }}>une cause</em><br />
            qui vous touche
          </h1>

          {/* Subtitle */}
          <p className="text-stone-300 text-base sm:text-lg max-w-xl mx-auto leading-relaxed">
            Découvrez les campagnes actives des synagogues partenaires.
            Chaque don ouvre droit à un reçu fiscal Cerfa et 66 % de réduction d'impôt.
          </p>
        </div>
      </section>

      {/* ─── SEARCH & FILTERS (floating sur le hero) ──────────────────── */}
      <div className="max-w-6xl mx-auto px-6 -mt-8 relative z-10">
        <div className="bg-white rounded-3xl shadow-xl shadow-stone-900/5 border border-stone-100 p-5 sm:p-6">
          {/* Search + Sort row */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3 items-center mb-5">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher une campagne, une synagogue…"
                className="w-full pl-11 pr-10 py-3.5 rounded-2xl bg-stone-50/50 border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/10 focus:bg-white transition-all placeholder:text-stone-400"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full bg-stone-200 hover:bg-stone-300 flex items-center justify-center transition-colors"
                  aria-label="Effacer la recherche"
                >
                  <X className="h-3 w-3 text-stone-600" />
                </button>
              )}
            </div>

            {/* Sort */}
            <div className="flex items-center gap-2 shrink-0">
              <SlidersHorizontal className="h-4 w-4 text-stone-400" />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-3 rounded-2xl border border-stone-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/10 transition-all font-medium text-stone-700 cursor-pointer hover:border-stone-300"
              >
                {SORTS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Type chips */}
          <div className="flex flex-wrap gap-2 pt-5 border-t border-stone-100">
            <FilterChip
              active={typeFilter === "all"}
              onClick={() => setTypeFilter("all")}
              label="Toutes"
              count={typeCounts.all}
              color="#0D1B33"
            />
            {Object.entries(TYPE_META).map(([key, meta]) => (
              <FilterChip
                key={key}
                active={typeFilter === key}
                onClick={() => setTypeFilter(key)}
                label={meta.label}
                count={typeCounts[key]}
                color={meta.color}
                Icon={meta.Icon}
              />
            ))}
            {activeFilters > 0 && (
              <button
                type="button"
                onClick={() => { setSearch(""); setTypeFilter("all"); }}
                className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium text-stone-500 hover:text-stone-900 transition-colors"
              >
                <X className="h-3 w-3" /> Réinitialiser
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ─── RESULTS ─────────────────────────────────────────────────── */}
      <div className="max-w-6xl mx-auto px-6 py-10 sm:py-14">
        {campaigns === null ? (
          <div className="flex items-center justify-center py-24">
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="h-8 w-8 animate-spin" style={{ color: "#B8962E" }} />
              <p className="text-sm text-stone-500">Chargement des campagnes…</p>
            </div>
          </div>
        ) : error ? (
          <EmptyState
            icon={Target}
            title="Impossible de charger les campagnes"
            subtitle="Vérifiez votre connexion et réessayez."
            actionLabel="Rafraîchir"
            onAction={() => window.location.reload()}
          />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Megaphone}
            title="Aucune campagne trouvée"
            subtitle={
              search || typeFilter !== "all"
                ? `Aucun résultat pour vos critères. Essayez d'élargir votre recherche.`
                : "Aucune campagne active pour le moment. Revenez bientôt !"
            }
            actionLabel={activeFilters > 0 ? "Réinitialiser les filtres" : null}
            onAction={() => { setSearch(""); setTypeFilter("all"); }}
          />
        ) : (
          <>
            {/* Results header */}
            <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
              <div>
                <h2
                  className="text-2xl sm:text-3xl text-stone-900 tracking-tight"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  {filtered.length} {filtered.length > 1 ? "campagnes" : "campagne"}
                  {activeFilters > 0 && (
                    <span className="text-stone-400 font-normal text-lg sm:text-xl ml-2">
                      · filtrées
                    </span>
                  )}
                </h2>
                <p className="text-xs text-stone-500 mt-1">
                  {sortBy === "recent" && "Triées par date de création"}
                  {sortBy === "popular" && "Triées par nombre de donateurs"}
                  {sortBy === "ending" && "Triées par date de fin"}
                  {sortBy === "progress" && "Triées par avancement"}
                </p>
              </div>
            </div>

            {/* Grid avec animation cascade */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((c, idx) => (
                <div
                  key={c.id}
                  style={{
                    animation: `cardFadeIn 0.5s ${Math.min(idx, 12) * 60}ms both ease-out`,
                  }}
                >
                  <CampaignCard campaign={c} />
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Animations CSS */}
      <style>{`
        @keyframes cardFadeIn {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
      `}</style>

      {/* Footer minimal */}
      <footer className="border-t border-stone-200/60 bg-white/50 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between flex-wrap gap-3 text-xs text-stone-500">
          <p>© {new Date().getFullYear()} Terouma · Plateforme de gestion des dons</p>
          <div className="flex gap-4">
            <Link to="/" className="hover:text-stone-900 transition-colors">Accueil</Link>
            <Link to="/contact" className="hover:text-stone-900 transition-colors">Contact</Link>
            <Link to="/kiddoush" className="hover:text-stone-900 transition-colors">Kiddoush</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ─── Stats display (hero) ──────────────────────────────────────────────
function Stat({ value, label, loading, compact }) {
  return (
    <div className="text-center">
      <p
        className={`font-light text-white tabular-nums tracking-tight ${
          compact ? "text-2xl sm:text-3xl" : "text-3xl sm:text-4xl"
        }`}
        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
      >
        {loading ? (
          <span className="inline-block w-12 h-7 bg-white/10 rounded animate-pulse" />
        ) : (
          value
        )}
      </p>
      <p className="text-[10px] uppercase tracking-[0.2em] text-stone-400 mt-1">{label}</p>
    </div>
  );
}

// ─── Filter chip ───────────────────────────────────────────────────────
function FilterChip({ active, onClick, label, count, color, Icon }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-medium transition-all duration-200 border ${
        active
          ? "text-white border-transparent shadow-md scale-[1.02]"
          : "bg-white text-stone-700 border-stone-200 hover:border-stone-300 hover:bg-stone-50"
      }`}
      style={active ? { background: color, boxShadow: `0 4px 12px ${color}33` } : undefined}
    >
      {Icon && <Icon className="h-3 w-3" />}
      <span>{label}</span>
      <span
        className={`tabular-nums px-1.5 rounded-full text-[10px] ${
          active ? "bg-white/20 text-white" : "bg-stone-100 text-stone-500"
        }`}
      >
        {count}
      </span>
    </button>
  );
}

// ─── Empty state ───────────────────────────────────────────────────────
function EmptyState({ icon: Icon, title, subtitle, actionLabel, onAction }) {
  return (
    <div className="text-center py-20 sm:py-28 px-4">
      <div
        className="h-20 w-20 rounded-3xl flex items-center justify-center mx-auto mb-6 border"
        style={{ background: "rgba(184,150,46,0.08)", borderColor: "rgba(184,150,46,0.2)" }}
      >
        <Icon className="h-9 w-9" style={{ color: "#B8962E" }} />
      </div>
      <h3
        className="text-2xl text-stone-900 mb-2"
        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
      >
        {title}
      </h3>
      <p className="text-sm text-stone-500 max-w-md mx-auto mb-6 leading-relaxed">{subtitle}</p>
      {actionLabel && onAction && (
        <button
          type="button"
          onClick={onAction}
          className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-medium text-white transition-all hover:scale-[1.02]"
          style={{ background: "#0D1B33", boxShadow: "0 4px 16px rgba(13,27,51,0.15)" }}
        >
          {actionLabel} <ArrowRight className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

// ─── Campaign card (premium) ───────────────────────────────────────────
function CampaignCard({ campaign }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const stats = campaign.stats || {};
  const theme = campaign.theme_color || "#0D1B33";
  const meta = TYPE_META[campaign.type] || TYPE_META.thermometer;
  const TypeIcon = meta.Icon;
  // Mêmes chiffres que la page campagne et la vignette WhatsApp : le PROMIS,
  // pourcentage du serveur arrondi vers le bas (lib/etatCampagne.js) ; les
  // paniers en unités.
  const carte = chiffresCarte(campaign);
  const pct = carte.objectif ? carte.pourcentage : null;
  const libelleUnite = campaign.unit_label || "unité";
  const endLabel = campaign.end_date
    ? new Date(campaign.end_date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })
    : null;
  // Prefer image_data_uri (embedded base64 in JSON) — works even if the
  // /image.png endpoint is flaky behind Vercel→Railway.
  const imgSrc = campaign.image_data_uri
    ? campaign.image_data_uri
    : (campaign.image_url
        ? (campaign.image_url.startsWith("http") ? campaign.image_url : `${BACKEND_BASE}${campaign.image_url}`)
        : null);

  // Règle du serveur (minuit heure de Paris) : jamais « Dernier jour » sur
  // une campagne terminée.
  const daysLeft = carte.joursRestants;

  return (
    <>
    <Link
      to={`/c/${campaign.id}`}
      className="group block bg-white border border-stone-200/80 rounded-3xl overflow-hidden hover:shadow-2xl hover:shadow-stone-900/8 hover:-translate-y-1 hover:border-stone-300 transition-all duration-300 no-underline"
    >
      {/* Hero image with gradient overlay. The small magnifier (top-right)
          opens a full-screen lightbox so donors can read poster-style
          portrait images without leaving the card. */}
      <div className="relative h-48 overflow-hidden">
        {imgSrc ? (
          <>
            <img
              src={imgSrc}
              alt={campaign.title}
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
            />
            <div
              className="absolute inset-0"
              style={{
                background: `linear-gradient(180deg, transparent 40%, ${theme}99 100%)`,
              }}
            />
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setLightboxOpen(true);
              }}
              className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold text-white bg-black/55 hover:bg-black/75 backdrop-blur-md transition-colors"
              aria-label="Voir l'image en grand"
              title="Voir l'image en grand"
            >
              <ZoomIn className="h-3 w-3" />
              Agrandir
            </button>
          </>
        ) : (
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(135deg, ${theme} 0%, ${theme}dd 50%, #B8962E 100%)`,
            }}
          >
            <div className="absolute inset-0 opacity-20" style={{
              backgroundImage: "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.3) 0%, transparent 50%), radial-gradient(circle at 70% 70%, rgba(255,255,255,0.2) 0%, transparent 50%)",
            }} />
            <div className="absolute inset-0 flex items-center justify-center">
              <TypeIcon className="h-16 w-16 text-white/30" />
            </div>
          </div>
        )}

        {/* Type badge (top-left) */}
        <div
          className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-semibold text-white backdrop-blur-md"
          style={{ background: "rgba(0,0,0,0.4)" }}
        >
          <TypeIcon className="h-3 w-3" />
          {meta.label}
        </div>

        {/* Days-left pill (top-right) */}
        {daysLeft !== null && daysLeft <= 14 && (
          <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-semibold text-white"
            style={{ background: daysLeft <= 3 ? "#DC2626" : "#EA580C" }}
          >
            <Calendar className="h-3 w-3" />
            {daysLeft === 0 ? "Dernier jour" : `J-${daysLeft}`}
          </div>
        )}

        {/* Bottom info (over gradient) */}
        <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
          {campaign.organization_name && (
            <p className="text-[10px] uppercase tracking-[0.15em] opacity-80 mb-1 truncate">
              {campaign.organization_name}
            </p>
          )}
          <h3
            className="font-medium text-lg leading-tight line-clamp-2"
            style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 500 }}
          >
            {campaign.title}
          </h3>
        </div>
      </div>

      {/* Body */}
      <div className="p-5">
        {/* Description */}
        <p className="text-xs text-stone-500 mb-4 line-clamp-2 leading-relaxed">
          {campaign.description
            ? campaign.description.replace(/<[^>]+>/g, "").slice(0, 130)
            : "Participez à cette campagne pour soutenir cette synagogue."}
        </p>

        {/* Progress */}
        {pct !== null && (
          <div className="mb-4">
            <div className="flex items-baseline justify-between mb-2">
              <div>
                {carte.unites ? (
                  <>
                    <span className="text-lg font-semibold text-stone-900 tabular-nums">{carte.valeur}</span>
                    <span className="text-xs text-stone-400 ml-1">
                      / {carte.objectif} {libelleUnite}{carte.objectif > 1 ? "s" : ""}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="text-lg font-semibold text-stone-900 tabular-nums">{fmtEUR(carte.valeur)}</span>
                    <span className="text-xs text-stone-400 ml-1">/ {fmtEUR(carte.objectif)}</span>
                  </>
                )}
              </div>
              <span
                className="text-xs font-semibold tabular-nums px-2 py-0.5 rounded-full"
                style={{ background: `${theme}10`, color: theme }}
              >
                {pct}%
              </span>
            </div>
            <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700 ease-out"
                style={{
                  width: `${carte.largeur}%`,
                  background: `linear-gradient(90deg, ${theme}, #B8962E)`,
                }}
              />
            </div>
          </div>
        )}

        {/* Meta footer */}
        <div className="flex items-center justify-between pt-4 border-t border-stone-100">
          <span className="inline-flex items-center gap-1.5 text-xs text-stone-500">
            <div className="h-6 w-6 rounded-full bg-stone-100 flex items-center justify-center">
              <Users className="h-3 w-3 text-stone-500" />
            </div>
            <span className="tabular-nums font-medium">{stats.donors_count || 0}</span>
            <span>donateur{(stats.donors_count || 0) > 1 ? "s" : ""}</span>
          </span>
          {endLabel && (
            <span className="inline-flex items-center gap-1 text-xs text-stone-500">
              <Calendar className="h-3 w-3" />
              Jusqu'au <strong className="text-stone-700">{endLabel}</strong>
            </span>
          )}
        </div>

        {/* CTA */}
        <div
          className="mt-4 flex items-center justify-center gap-1.5 py-3 rounded-2xl text-sm font-semibold text-white transition-all group-hover:gap-2.5"
          style={{
            background: theme,
            boxShadow: `0 4px 14px ${theme}33`,
          }}
        >
          Participer
          <ChevronRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
        </div>
      </div>
    </Link>
    <CampaignImageLightbox
      src={imgSrc}
      alt={campaign.title}
      open={lightboxOpen}
      onClose={() => setLightboxOpen(false)}
    />
    </>
  );
}
